import { useEffect, useMemo, useRef, useState } from "react";

import useSignalrAiChat from "../hooks/useSignalrAiChat";
import useI18n from "../i18n/useI18n";
import { trackEvent } from "../utils/analytics";

// Dark "CodeIL Architect" chat panel. UI only — streaming, connection and
// history come from the real SignalR hook, unchanged.
const AiLeadQualifier = () => {
  const { t } = useI18n();
  const {
    connectionStatus,
    messages,
    streamingChunk,
    isReceiving,
    appendUserMessage,
    sendMessage,
  } = useSignalrAiChat();

  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const chatRef = useRef(null);

  const showTypingOnly = isReceiving && !streamingChunk;

  const statusText = useMemo(() => {
    if (connectionStatus === "connected") return t("ai.statusConnected");
    if (connectionStatus === "connecting") return t("ai.statusConnecting");
    if (connectionStatus === "error") return t("ai.statusError");
    return t("ai.statusIdle");
  }, [connectionStatus, t]);

  useEffect(() => {
    const box = chatRef.current;
    if (box) {
      box.scrollTop = box.scrollHeight;
    }
  }, [messages, streamingChunk, showTypingOnly]);

  const submit = async (content) => {
    const text = (content || "").trim();
    if (!text || isSubmitting || isReceiving) {
      return;
    }
    const history = appendUserMessage(text);
    setInputValue("");
    setIsSubmitting(true);
    trackEvent("ai_qualifier_idea_submitted", {
      characters: text.length,
      connection_status: connectionStatus,
    });
    try {
      await sendMessage(history);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSend =
    inputValue.trim().length > 0 && !isSubmitting && !isReceiving && connectionStatus === "connected";
  const chips = t("ai.chips");

  return (
    <div className="ai-chat">
      <div className="ai-chat__bar">
        <span className={`ai-chat__status-dot ai-chat__status-dot--${connectionStatus}`} />
        <span className="ai-chat__title">{t("ai.panelTitle")}</span>
        <span className="ai-chat__status">{statusText}</span>
      </div>

      <div className="ai-chat__messages" ref={chatRef} aria-live="polite">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`ai-bubble ai-bubble--${message.role === "assistant" ? "bot" : "user"}`}
          >
            <span className="ai-bubble__who">
              {message.role === "assistant" ? t("ai.architect") : t("ai.you")}
            </span>
            <p>{message.content}</p>
          </div>
        ))}

        {streamingChunk && (
          <div className="ai-bubble ai-bubble--bot">
            <span className="ai-bubble__who">{t("ai.architect")}</span>
            <p>{streamingChunk}</p>
          </div>
        )}

        {showTypingOnly && (
          <div className="ai-typing" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      <div className="ai-chat__chips">
        {Array.isArray(chips) &&
          chips.map((chip, i) => (
            <button
              key={i}
              type="button"
              className="ai-chip"
              onClick={() => submit(chip)}
              disabled={isSubmitting || isReceiving || connectionStatus !== "connected"}
            >
              {chip}
            </button>
          ))}
      </div>

      <form
        className="ai-chat__composer"
        onSubmit={(event) => {
          event.preventDefault();
          submit(inputValue);
        }}
      >
        <label htmlFor="ai-input" className="visually-hidden">
          {t("ai.panelTitle")}
        </label>
        <input
          id="ai-input"
          className="ai-chat__input"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          placeholder={t("ai.inputPlaceholder")}
          disabled={connectionStatus !== "connected" || isSubmitting}
        />
        <button type="submit" className="ai-chat__send" disabled={!canSend}>
          {isSubmitting || isReceiving ? t("ai.sending") : t("ai.send")}
        </button>
      </form>
    </div>
  );
};

export default AiLeadQualifier;
